import React, { useState } from "react";
import { useLocation, useParams } from "react-router";
import { Button, Form } from "react-bootstrap";

import {
  emptyString,
  KEYBOARD_KEYS,
  BUTTON_TEXT,
  questionTypeConstants,
  surveyQuestionsConstants,
  archivedQuestionsConstants,
  STYLES,
  surveyQuestionValidation,
} from "../../../constants";

import {
  handleSpace,
  archiveQuestionConfirmationModal,
  onKeyDownHandler,
} from "../../../../../shared";
import ArchiveButton from "../shared/archiveButton";
import useModalDisplay from "../../../../../shared/components/hooks/useModalDisplay/useModalDisplay";
import {
  updateQuestionByOrder,
  archiveAndReorderQuestions,
} from "../utils/surveyQuestionHelpers";

const {
  CHAR_LIMIT_MIN,
  CHAR_LIMIT_MAX_SINGLE_DIGIT,
  FIRST_QUESTION_ORDER,
  RESTRICTED_CHAR_LIMIT_KEYS,
} = surveyQuestionValidation;

const TextBox = (props) => {
  const {
    index,
    errors,
    handle,
    register,
    question,
    localRole,
    questionList,
    setIsChanges,
    setQuestionList,
    handleSurveySubmit,
    handleRemoveQuestion,
    handleRecipientsUser,
    confirmReactivateArchiveQuestion,
  } = props;

  const { charLimit, questionText, questionOrder } = question;

  const { brandId } = useParams();
  const location = useLocation();

  const isArchivedPage = location.pathname.includes(
    archivedQuestionsConstants.ARCHIVED_QUESTIONS,
  );

  const [questionValue, setQuestionValue] = useState({
    questionText: questionText || null,
    charLimit: charLimit,
  });
  const isDisabled = localRole || question.isReadOnly || isArchivedPage;
  const isArchiveDisabled = localRole || !question.isReadOnly;
  const [showModal, modalElement, setComponent, closeModal] = useModalDisplay();

  //Function to handle question field in textbox
  const handleChangePrimaryTextBoxText = (e) => {
    const { value } = e.target;
    setQuestionValue((prev) => ({ ...prev, questionText: value }));
    setQuestionList((prevList) => {
      const questionArray = updateQuestionByOrder(
        prevList,
        questionOrder,
        (obj) => ({
          ...obj,
          questionText: value,
          isEdit: !obj.isNew,
        }),
      );
      return handleRecipientsUser(questionArray);
    });
  };

  const handleChangePrimaryTextBoxCharLimit = (e) => {
    const { value } = e.target;
    setQuestionValue((prev) => ({ ...prev, charLimit: value }));
    setQuestionList((prevList) => {
      const questionArray = updateQuestionByOrder(
        prevList,
        questionOrder,
        (obj) => ({
          ...obj,
          charLimit: value,
          isEdit: !obj.isNew,
        }),
      );
      return handleRecipientsUser(questionArray);
    });
  };

  // Prevents the input of specific characters and limits the minimum value to 1 and maximum value to 9
  const handleKeyDown = (e) => {
    const currentValue = parseInt(e.target.value, 10);

    if (
      RESTRICTED_CHAR_LIMIT_KEYS.includes(e.key) ||
      (e.key === KEYBOARD_KEYS.BACKSPACE &&
        (currentValue === CHAR_LIMIT_MIN ||
          currentValue < CHAR_LIMIT_MAX_SINGLE_DIGIT))
    ) {
      e.preventDefault();
    }
  };

  //If user confirm then check validation and save survey
  const archiveQuestionAndSaveSurvey = () => {
    const questionArrayWithOutArchivedQues = archiveAndReorderQuestions(
      questionList,
      questionOrder,
      question.id,
    );
    handleSurveySubmit(questionArrayWithOutArchivedQues, question.id);
  };

  //Show confirmation modal when user click on archive modal
  const confirmArchiveQuestion = () => {
    setComponent(
      archiveQuestionConfirmationModal({
        onClose: closeModal,
        onConfirm: archiveQuestionAndSaveSurvey,
        message:
          "Would you like to archive this question? Archived questions can be reactivated for later use",
      }),
    );
  };

  const removeQuestionHandler = () => {
    handleRemoveQuestion(questionOrder, `${question.guid}`, question?.id);
  };

  const hideRemoveButton =
    isDisabled ||
    questionOrder === FIRST_QUESTION_ORDER ||
    questionText === surveyQuestionsConstants.NPS_TEXT_QUESTION;

  return (
    <>
      {/*Modal here */}
      {showModal && modalElement}
      <div
        className="notification-form shadow card mt-3"
        data-testid="survey-questions"
      >
        <div className="d-flex justify-content-between" {...handle}>
          <div
            role="button"
            tabIndex={0}
            data-testid="remove-survey-question"
            className={`remove-button ms-1 fs-6 fw-bold ${
              hideRemoveButton ? STYLES.D_NONE : emptyString
            }`}
            onClick={() => removeQuestionHandler()}
            onKeyDown={(e) => {
              onKeyDownHandler(e, () => {
                removeQuestionHandler();
              });
            }}
          >
            ✖
          </div>
          <div className="d-flex align-items-center ms-auto">
            <ArchiveButton
              index={question.guid}
              questionText={questionText}
              onClick={confirmArchiveQuestion}
              isDisabled={isArchiveDisabled || isArchivedPage}
            />
            <div className="question-label ms-auto align-self-start">
              {questionTypeConstants.TEXTBOX}
            </div>
          </div>
        </div>
        <div className="card-body main-question-div  px-4 pt-0">
          <h5>{`Question ${index}:`}</h5>
          <Form.Control
            type="text"
            name={`${question.guid}`}
            id={`${question.guid}`}
            placeholder="How was your online ordering experience?"
            value={questionText || questionValue.questionText}
            {...register(`${question.guid}`, {
              required: !isDisabled,
              onChange: (e) => {
                handleChangePrimaryTextBoxText(e);
                setIsChanges(true);
              },
              disabled:
                questionText === surveyQuestionsConstants.NPS_TEXT_QUESTION ||
                isDisabled,
            })}
            onKeyDown={(e) => handleSpace(e)}
            className={`${
              errors[`${question.guid}`] ? STYLES.IS_INVALID : emptyString
            }`}
          />
          <div className="d-flex align-items-center mt-2">
            <Form.Label htmlFor="charLimit" className="mt-1">
              Character Limit:
            </Form.Label>
            <input
              type="number"
              id="charLimit"
              min={CHAR_LIMIT_MIN}
              value={questionValue.charLimit}
              onChange={(e) => {
                handleChangePrimaryTextBoxCharLimit(e);
                setIsChanges(true);
              }}
              className="form-control ms-1 char-limited-field "
              disabled={isDisabled}
              onKeyDown={(e) => handleKeyDown(e)}
              onPaste={(e) => e.preventDefault()}
            />
          </div>

          <div className="d-flex justify-content-between">
            <Button
              className={`border-0 px-4 ms-auto green-background-color ${
                isArchivedPage ? emptyString : STYLES.D_NONE
              }`}
              onClick={() =>
                confirmReactivateArchiveQuestion(brandId, question.id)
              }
            >
              {BUTTON_TEXT.REACTIVATE}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default TextBox;
